import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { PrimaryButton, SecondaryButton } from './index';
import {
  Add as AddIcon,
  AutoAwesome,
  PlayArrow,
  Stop,
  Assessment,
  Edit,
  Delete,
  Visibility
} from '@mui/icons-material';
import PLRService, { RegraPLR, PLRGeracaoDto, ResultadoPLR } from '../services/PLRService';
import OKRService, { OKR } from '../services/OKRService';

const PLRList: React.FC = () => {
  const [regras, setRegras] = useState<RegraPLR[]>([]);
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openGerarDialog, setOpenGerarDialog] = useState(false);
  const [openResultadosDialog, setOpenResultadosDialog] = useState(false);
  const [resultados, setResultados] = useState<ResultadoPLR[]>([]);
  const [novaRegra, setNovaRegra] = useState<Partial<RegraPLR>>({
    nome: '',
    periodo: '',
    status: 'ABERTA',
    colaboradores: [],
    valorTotalDisponivel: 0
  });
  const [geracaoData, setGeracaoData] = useState<PLRGeracaoDto>({
    okrIds: [],
    periodo: '',
    valorTotalDisponivel: 0
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [regrasData, okrsData] = await Promise.all([
        PLRService.listar(),
        OKRService.listar()
      ]);
      setRegras(regrasData);
      setOkrs(okrsData);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCriarRegra = async () => {
    try {
      if (!novaRegra.nome || !novaRegra.periodo) {
        setError('Nome e período são obrigatórios');
        return;
      }

      await PLRService.criar(novaRegra as Omit<RegraPLR, 'id'>);
      setOpenDialog(false);
      setNovaRegra({
        nome: '',
        periodo: '',
        status: 'ABERTA',
        colaboradores: [],
        valorTotalDisponivel: 0
      });
      carregarDados();
    } catch (err) {
      setError('Erro ao criar regra de PLR');
      console.error(err);
    }
  };

  const handleGerarRegra = async () => {
    try {
      if (!geracaoData.periodo || geracaoData.okrIds.length === 0) {
        setError('Período e pelo menos um OKR são obrigatórios');
        return;
      }

      const regraGerada = await PLRService.gerarRegra(geracaoData);
      await PLRService.criar(regraGerada);
      
      setOpenGerarDialog(false);
      setGeracaoData({
        okrIds: [],
        periodo: '',
        valorTotalDisponivel: 0
      });
      carregarDados();
    } catch (err) {
      setError('Erro ao gerar regra de PLR');
      console.error(err);
    }
  };

  const handleFecharPeriodo = async (id: string) => {
    try {
      await PLRService.fecharPeriodo(id);
      carregarDados();
    } catch (err) {
      setError('Erro ao fechar período');
      console.error(err);
    }
  };

  const handleProcessarResultados = async (id: string) => {
    try {
      await PLRService.processarResultados(id);
      carregarDados();
    } catch (err) {
      setError('Erro ao processar resultados');
      console.error(err);
    }
  };

  const handleVerResultados = async (regraId: string) => {
    try {
      const resultadosData = await PLRService.obterResultadosRegra(regraId);
      setResultados(resultadosData);
      setOpenResultadosDialog(true);
    } catch (err) {
      setError('Erro ao obter resultados');
      console.error(err);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' } = {
      'ABERTA': 'info',
      'FECHADA': 'warning',
      'PROCESSADA': 'success'
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Carregando regras de PLR...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          PLR (Participação nos Lucros e Resultados)
        </Typography>
        <Box>
          <SecondaryButton
            variant="contained"
            startIcon={<AutoAwesome />}
            onClick={() => setOpenGerarDialog(true)}
            sx={{ mr: 2 }}
          >
            Gerar com IA
          </SecondaryButton>
          <PrimaryButton
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Nova Regra
          </PrimaryButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {regras.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Assessment sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhuma regra de PLR encontrada
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Comece criando sua primeira regra de PLR
            </Typography>
            <PrimaryButton
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Criar Primeira Regra
            </PrimaryButton>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {regras.map((regra) => (
            <Grid size={{xs: 12, md: 6, lg: 4}} key={regra.id}>
              <Card sx={{ height: '100%' }}>
                <CardHeader
                  title={regra.nome}
                  subheader={
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        label={regra.periodo}
                        color="primary"
                        size="small"
                      />
                      <Chip
                        label={regra.status}
                        color={getStatusColor(regra.status)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                />
                <CardContent>
                  {regra.valorTotalDisponivel && (
                    <Typography variant="h6" color="primary" gutterBottom>
                      {formatCurrency(regra.valorTotalDisponivel)}
                    </Typography>
                  )}
                  
                  {regra.okrsPesos && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {regra.okrsPesos.length} OKR(s) vinculado(s)
                    </Typography>
                  )}

                  {regra.colaboradores && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {regra.colaboradores.length} colaborador(es)
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {regra.status === 'ABERTA' && (
                      <Tooltip title="Fechar período">
                        <IconButton
                          size="small"
                          onClick={() => handleFecharPeriodo(regra.id!)}
                          color="warning"
                        >
                          <Stop />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {regra.status === 'FECHADA' && (
                      <Tooltip title="Processar resultados">
                        <IconButton
                          size="small"
                          onClick={() => handleProcessarResultados(regra.id!)}
                          color="success"
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {regra.status === 'PROCESSADA' && (
                      <Tooltip title="Ver resultados">
                        <IconButton
                          size="small"
                          onClick={() => handleVerResultados(regra.id!)}
                          color="primary"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog para criar nova regra */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Criar Nova Regra de PLR</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome da Regra"
              value={novaRegra.nome}
              onChange={(e) => setNovaRegra({ ...novaRegra, nome: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Período"
              placeholder="Ex: 2025-T1, 2025-Anual"
              value={novaRegra.periodo}
              onChange={(e) => setNovaRegra({ ...novaRegra, periodo: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Valor Total Disponível"
              type="number"
              value={novaRegra.valorTotalDisponivel}
              onChange={(e) => setNovaRegra({ ...novaRegra, valorTotalDisponivel: parseFloat(e.target.value) })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Critérios Adicionais"
              multiline
              rows={3}
              value={novaRegra.criteriosAdicionais || ''}
              onChange={(e) => setNovaRegra({ ...novaRegra, criteriosAdicionais: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <PrimaryButton onClick={() => setOpenDialog(false)}>Cancelar</PrimaryButton>
          <SecondaryButton onClick={handleCriarRegra}>
            Criar Regra
          </SecondaryButton>
        </DialogActions>
      </Dialog>

      {/* Dialog para gerar regra com IA */}
      <Dialog open={openGerarDialog} onClose={() => setOpenGerarDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gerar Regra de PLR com IA</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Período"
              placeholder="Ex: 2025-T1, 2025-Anual"
              value={geracaoData.periodo}
              onChange={(e) => setGeracaoData({ ...geracaoData, periodo: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Valor Total Disponível"
              type="number"
              value={geracaoData.valorTotalDisponivel}
              onChange={(e) => setGeracaoData({ ...geracaoData, valorTotalDisponivel: parseFloat(e.target.value) })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>OKRs</InputLabel>
              <Select
                multiple
                value={geracaoData.okrIds}
                label="OKRs"
                onChange={(e) => setGeracaoData({ ...geracaoData, okrIds: e.target.value as string[] })}
              >
                {okrs.map((okr) => (
                  <MenuItem key={okr.id} value={okr.id}>
                    {okr.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Contexto Adicional"
              multiline
              rows={3}
              value={geracaoData.contextoAdicional || ''}
              onChange={(e) => setGeracaoData({ ...geracaoData, contextoAdicional: e.target.value })}
              placeholder="Informações adicionais para a geração da regra..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <PrimaryButton onClick={() => setOpenGerarDialog(false)}>Cancelar</PrimaryButton>
          <SecondaryButton onClick={handleGerarRegra}>
            Gerar Regra
          </SecondaryButton>
        </DialogActions>
      </Dialog>

      {/* Dialog para visualizar resultados */}
      <Dialog open={openResultadosDialog} onClose={() => setOpenResultadosDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Resultados da PLR</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Colaborador</TableCell>
                  <TableCell>Progresso Geral</TableCell>
                  <TableCell>Valor Calculado</TableCell>
                  <TableCell>Valor Final</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultados.map((resultado) => (
                  <TableRow key={resultado.id}>
                    <TableCell>{resultado.colaboradorNome}</TableCell>
                    <TableCell>{resultado.progressoGeral.toFixed(1)}%</TableCell>
                    <TableCell>{formatCurrency(resultado.valorCalculado)}</TableCell>
                    <TableCell>{formatCurrency(resultado.valorFinal || resultado.valorCalculado)}</TableCell>
                    <TableCell>
                      <Chip
                        label={resultado.status}
                        color={resultado.status === 'PAGO' ? 'success' : 'info'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <PrimaryButton onClick={() => setOpenResultadosDialog(false)}>Fechar</PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PLRList;
